import React from 'react';
import SiteNode from './siteNode';

class SiteSelector extends React.Component {
  prevSelectionMostTopParentId = null;
  state = {
    siteByNodeIdMap: {},
    topLevelNodes: [],
    forceUpdate: false
  };

  async componentDidMount() {
    const { sitesSelected, filterString } = this.props;
    const siteByNodeIdMap = {};
    let topLevelNodes = {};

    const sitesList = this.props.hierarchy;
    const allSitesIds = new Set();
    for (let eachSite of sitesList) {
      allSitesIds.add(eachSite.id);
    }

    const activeSites = sitesList.filter((site) => site.active === 1);
    for (const site of activeSites) {
      siteByNodeIdMap[site.id] = { node: site, isSelected: false, childCountSelected: 0, children: [], isExpanded: false };
    }

    for (const selection of sitesSelected) {
      if (siteByNodeIdMap[selection]) {
        siteByNodeIdMap[selection].isSelected = true;
      }
    }

    for (const site of activeSites) {
      if (siteByNodeIdMap[site.parentId]) {
        siteByNodeIdMap[site.parentId].children.push(site);
      }
    }

    topLevelNodes = activeSites.filter((site) => !allSitesIds.has(site.parentId));

    if (sitesSelected && sitesSelected.length) {
      for (const site of activeSites) {
        if (siteByNodeIdMap[site.id].children.length > 0) {
          this.restoreSelection(site.id, siteByNodeIdMap);
        }
      }
    }

    this.filterItems(siteByNodeIdMap, filterString, this.props.selectableFn);

    this.setState({
      siteByNodeIdMap,
      topLevelNodes
    });
  }

  changeSelection(ids) {
    const siteByNodeIdMap = this.state.siteByNodeIdMap;
    for (let id in siteByNodeIdMap) {
      siteByNodeIdMap[id].isSelected = false;
      siteByNodeIdMap[id].childCountSelected = 0;
    }
    for (const id of ids) {
      siteByNodeIdMap[id].isSelected = true;
    }

    for (let node of this.state.topLevelNodes) {
      this.restoreSelection(node.id, siteByNodeIdMap);
    }

    this.setState({ siteByNodeIdMap, forceUpdate: !this.state.forceUpdate });
  }

  isFilteringItemsActive(filterString) {
    return filterString && filterString.length >= 3 ? true : false;
  }

  filterItems(siteByNodeIdMap, filterString, selectableFn) {
    const useFiltering = this.isFilteringItemsActive(filterString);
    const isSingleSelect = this.props.isSingleSelect;

    for (let id in siteByNodeIdMap) {
      let selectable = !selectableFn || (selectableFn(siteByNodeIdMap[id].node));
      let highlighted = siteByNodeIdMap[id].node.name.toLowerCase().indexOf(filterString.toLowerCase()) !== -1;
      let hidden = !selectable || (useFiltering && !highlighted);

      siteByNodeIdMap[id].isExpanded = false;
      siteByNodeIdMap[id].childCountSelectable = 0;
      siteByNodeIdMap[id]._previousSelectable = siteByNodeIdMap[id].selectable;
      siteByNodeIdMap[id].selectable = selectable;
      siteByNodeIdMap[id]._previousHidden = siteByNodeIdMap[id].hidden;
      siteByNodeIdMap[id].hidden = hidden;
      siteByNodeIdMap[id]._highlighted = highlighted;
    }

    //update parent nodes
    for (let id in siteByNodeIdMap) {
      let parentId = siteByNodeIdMap[id].node.parentId;
      if (parentId) {
        while (parentId && siteByNodeIdMap[parentId]) {
          let parentNode = siteByNodeIdMap[parentId];
          if (parentNode) {
            if (siteByNodeIdMap[id].selectable === true) {
              if (useFiltering && !isSingleSelect && parentNode._highlighted) {
                //we can show parent because it matches filter and has a selectable child
                parentNode.hidden = false;
              }
              parentNode.childCountSelectable += 1;
            }
            if (siteByNodeIdMap[id].hidden === false) {
              //make sure parent is shown in order to see this chid
              parentNode.hidden = false;
              if (useFiltering) {
                //expend everything when in filter mode
                parentNode.isExpanded = true;
              }
            }
            parentId = siteByNodeIdMap[parentId].node.parentId;
          }
          else {
            parentId = false;
          }
        }
      }
    }

    //need to return true if we need to rerender, so if nodes have appeared/disappeared or if filter has changed
    for (let id in siteByNodeIdMap) {
      if (siteByNodeIdMap[id]._previousHidden !== siteByNodeIdMap[id].hiden) {
        return true;
      }
    }
    return useFiltering;
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.topLevelNodes.length !== nextState.topLevelNodes.length) return true;
    if (nextState.forceUpdate !== this.state.forceUpdate) return true;
    return this.filterItems(this.state.siteByNodeIdMap, nextProps.filterString, this.props.selectableFn);
  }

  handleSingleChange(nodeId, newValue, parentsChain) {
    const siteByNodeIdMap = this.state.siteByNodeIdMap;

    if (newValue && this.props.isSingleSelect) {
      for (let id in siteByNodeIdMap) {
        siteByNodeIdMap[id].isSelected = false;
        siteByNodeIdMap[id].childCountSelected = 0;
      }
      if (this.prevSelectionMostTopParentId) {
        siteByNodeIdMap[this.prevSelectionMostTopParentId].refreshSelected(false);
      }
      this.prevSelectionMostTopParentId = parentsChain?.length ? parentsChain[parentsChain.length - 1].id : nodeId;
    }
    else {
      this.prevSelectionMostTopParentId = null;
    }

    siteByNodeIdMap[nodeId].isSelected = siteByNodeIdMap[nodeId].selectable ? newValue : false;
    this.countSelectionRecursivelyAllParents(parentsChain);

    const ids = [];
    for (let id in siteByNodeIdMap) {
      if (siteByNodeIdMap[id].isSelected) {
        ids.push(parseInt(id));
      }
    }
    if (this.props.onChangeSelection) {
      this.props.onChangeSelection(ids);
    }
  }

  handleRecursiveChange(nodeId, newValue, parentsChain) {
    const siteByNodeIdMap = this.state.siteByNodeIdMap;

    const wasSelected = siteByNodeIdMap[nodeId].isSelected;
    this.changeSelectionRecursivelyAllChildren(nodeId, newValue);
    siteByNodeIdMap[nodeId].isSelected = wasSelected;
    this.countSelectionRecursivelyAllParents(parentsChain);

    const ids = [];
    for (let id in siteByNodeIdMap) {
      if (siteByNodeIdMap[id].isSelected) {
        ids.push(parseInt(id));
      }
    }
    if (this.props.onChangeSelection) {
      this.props.onChangeSelection(ids);
    }
  }

  countSelectionRecursivelyAllParents(parentsChain) {
    const siteByNodeIdMap = this.state.siteByNodeIdMap;
    let node = parentsChain.shift();
    while (node) {
      const parentId = node.id;
      this.countSelection(parentId, siteByNodeIdMap);
      node = parentsChain.shift();
    }
  }

  countSelection(parentId, siteByNodeIdMap) {
    let selectedChildren = 0;
    for (const child of siteByNodeIdMap[parentId].children) {
      if (siteByNodeIdMap[child.id].children.length > 0) {
        selectedChildren += (siteByNodeIdMap[child.id].isSelected ? 1 : 0) + siteByNodeIdMap[child.id].childCountSelected;
      }
      else {
        selectedChildren += siteByNodeIdMap[child.id].isSelected ? 1 : 0;
      }
    }
    siteByNodeIdMap[parentId].childCountSelected = selectedChildren;
  }

  restoreSelection(nodeId, siteByNodeIdMap) {
    const node = siteByNodeIdMap[nodeId];
    if (node.children.length > 0) {
      for (let site of node.children) {
        this.restoreSelection(site.id, siteByNodeIdMap);
      }
      this.countSelection(nodeId, siteByNodeIdMap);
    }
  }

  changeSelectionRecursivelyAllChildren(id, newValue) {
    const siteByNodeIdMap = this.state.siteByNodeIdMap;
    const children = siteByNodeIdMap[id].children;
    let totalSelection = 0;

    siteByNodeIdMap[id].isSelected = siteByNodeIdMap[id].selectable ? newValue : false;

    if (children.length) {
      for (let child of children) {
        totalSelection += this.changeSelectionRecursivelyAllChildren(child.id, newValue);
      }
    }
    else {
      return 0;
    }
    if (newValue === 0) {
      totalSelection = 0;
    }

    siteByNodeIdMap[id].childCountSelected = totalSelection;

    return totalSelection;
  }

  renderNoResults() {
    if (this.state.topLevelNodes.every((node) => this.state.siteByNodeIdMap[node.id].hidden === true)) {
      return <div className='m-2'>No sites found</div>;
    }
    return null;
  }

  render() {
    const useFiltering = this.isFilteringItemsActive(this.props.filterString);

    return (
      <div>
        {this.state.topLevelNodes.map((node) => (
          <SiteNode
            key={node.id}
            node={node}
            level={1}
            siteByNodeIdMap={this.state.siteByNodeIdMap}
            filterString={this.props.filterString}
            useFiltering={useFiltering}
            hidden={this.state.siteByNodeIdMap[node.id].hidden}
            selectable={this.state.siteByNodeIdMap[node.id].selectable}
            isSingleSelect={this.props.isSingleSelect}
            onRecursiveChange={(id, newValue, parentsChain) => this.handleRecursiveChange(id, newValue, parentsChain)}
            onSingleChange={(id, newValue, parentsChain) => this.handleSingleChange(id, newValue, parentsChain)}
          />
        ))}
        {this.renderNoResults()}
      </div>
    );
  }
}

export default SiteSelector;
